<script setup>
import {ref} from "vue";
import * as yup from "yup";
import {useMutation} from "@vue/apollo-composable";
import {requiredMessage, stringValidator} from "@/utils/yupValidators";
import {useDjangoForm} from "@/utils/forms";
import {displayValue} from "@/utils/utils";
import {InfolinePatientsSearchMutation} from "@/graphql/mutations/hpo/infoline/patient_search.graphql";
import {mdiCardAccountDetailsOutline, mdiMagnify} from "@mdi/js";
import PatientDetailModal from "./PatientDetailModal.vue";

const props = defineProps({
  hpo: {
    type: Object,
    required: true,
  },
  caller: {
    type: Object,
    required: true,
  },
  member: {
    type: Object,
    required: true,
  },
  countries: {
    type: Array,
    required: true,
  },
});

const patientDetailId = ref(null)

const {defineField, handleMutationSubmit, nonFieldErrors} = useDjangoForm({
  validationSchema: yup.object({
    lastName: stringValidator.required(requiredMessage),
  }),
  // initialValues: {
  //   lastName: "Nový",
  // }
});

const [lastName, lastNameProps] = defineField('lastName');

const {mutate, loading} = useMutation(InfolinePatientsSearchMutation);
const patients = ref([]);

const onSubmit = handleMutationSubmit(
  (values) => mutate({...values, hpo: props.hpo.id, caller: props.caller.id}),
  {
    onSuccess(response) {
      patients.value = response.patients;
    },
  },
);
</script>

<template>
  <v-row>
    <v-col cols="12">
      <v-card :title="pgettext('label', 'Caller')"
              :prepend-icon="mdiCardAccountDetailsOutline">
        <v-card-text>
          <dl>
            <dt>{{ pgettext('label', 'Last Name') }}</dt>
            <dd>{{ caller.lastName }}</dd>
            <dt>{{ pgettext('label', 'First Name') }}</dt>
            <dd>{{ caller.firstName }}</dd>
            <dt>{{ pgettext('label', 'Phone Number') }}</dt>
            <dd>{{ caller.phoneNumber }}</dd>
          </dl>
        </v-card-text>
      </v-card>
      <v-card>
        <v-card-text>
          <v-form @submit.prevent="onSubmit">
            <v-row>
              <v-col class="d-flex flex-column ga-2">

                <v-alert v-for="message in nonFieldErrors"
                         :key="message"
                         :title="message"
                         type="error"/>

                <v-text-field v-model="lastName"
                              v-bind="lastNameProps"
                              :label="pgettext('label','Patient\'s Last Name')"
                              :placeholder="pgettext('instruction', 'Put in patient\'s last name')"
                              autofocus
                              clearable/>

                <v-btn type="submit"
                       block
                       size="x-large"
                       :loading="loading"
                       :disabled="!lastName"
                       :prepend-icon="mdiMagnify"
                       :text="pgettext('action', 'Search')"></v-btn>

              </v-col>
            </v-row>
          </v-form>

          <v-card>
            <v-card-text>
              <v-table>
                <thead>
                <tr>
                  <th>{{ pgettext('label', 'Last Name') }}</th>
                  <th>{{ pgettext('label', 'First Name') }}</th>
                  <th>{{ pgettext('label', 'Birth Year') }}</th>
                  <th>{{ pgettext('label', 'City') }}</th>
                  <th></th>
                </tr>
                </thead>
                <tbody>
                <template v-if="patients.length > 0">
                  <tr v-for="patient in patients" :key="patient.id">
                    <td>{{ displayValue(patient.lastName) }}</td>
                    <td>{{ displayValue(patient.firstName) }}</td>
                    <td>{{ displayValue(patient.birthYear) }}</td>
                    <td>{{ displayValue(patient.city) }}</td>
                    <td>
                      <v-btn :text="pgettext('action', 'View Detail')"
                             @click="patientDetailId=patient.id"
                             block
                      />
                    </td>
                  </tr>
                </template>
                <tr v-else>
                  <td colspan="5">
                    <v-empty-state
                                   :title="pgettext('label', 'No results')"
                                   :icon="mdiMagnify"
                    />
                  </td>
                </tr>
                </tbody>
              </v-table>
            </v-card-text>
          </v-card>


        </v-card-text>
      </v-card>


      <PatientDetailModal v-if="patientDetailId"
                          :patient-id="patientDetailId"
                          :hpo="hpo"
                          :member="member"
                          :countries="countries"
                          @close="patientDetailId=null"/>


    </v-col>
  </v-row>
</template>

<style scoped>
</style>
